<template>
  <div class="h-full w-full">
    <TargetAreasExplorer
      v-if="token"
      :api-base-url="apiBaseUrl"
      :api-key="token"
      :country-iso3="countryIso3"
      :marker-points="markerPoints"
      :marker-label="t('views.training_center_group.target_areas.show_training_centers')"
      :popup-actions="popupActions"
      :on-token-refresh="handleTokenRefresh"
      :on-auth-failure="handleAuthFailure"
    />
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { TargetAreasExplorer } from '@ishare/kestrel-vue'
import i18n from '@utils/i18n'
import FlashMessageService from '@services/FlashMessages'
import { useSparrowAuth } from '@composables/useSparrowAuth'

const { t } = i18n.global

const props = defineProps({
  targetAreasData: {
    type: Array,
    required: true,
  },
  apiData: {
    type: Object,
    required: true,
  },
})

// ─── Derived config ───────────────────────────────────────────────────────────

const countryIso3 = computed(() => props.targetAreasData[0]?.country_iso3_code)
const apiBaseUrl = computed(() => (props.apiData?.kestrel_api_url || '').replace(/\/api\/?$/, ''))

// ─── Auth token ───────────────────────────────────────────────────────────────

const { token, fetchToken, handleTokenRefresh, handleAuthFailure } = useSparrowAuth({
  countryIso3,
  capabilities: ['target_area.view', 'boundary.view:all', 'boundary.view_shading:all'],
  onError: () => FlashMessageService.error('Map authentication failed. Please reload the page.'),
})

// ─── Marker points (training center locations) ────────────────────────────────

const markerPoints = computed(() =>
  props.targetAreasData
    .filter((tc) => tc.lat && tc.long && tc.target_area_external_id)
    .map((tc) => ({
      id: `tc-${tc.training_center_id}`,
      lat: parseFloat(tc.lat),
      lng: parseFloat(tc.long),
      color: '#2563eb',
      scale: 0.6,
    }))
)

// ─── Popup actions ────────────────────────────────────────────────────────────

function findTrainingCenterId(ta) {
  const externalId = ta.externalId
  const match = props.targetAreasData.find(
    (tc) => tc.target_area_external_id && tc.target_area_external_id === externalId
  )
  if (!match) {
    console.warn(
      '[TargetAreasV2] No matching training center for externalId:',
      externalId,
      'Available external IDs:',
      props.targetAreasData.map((tc) => tc.target_area_external_id)
    )
  }
  return match?.training_center_id
}

function navigateTo(url) {
  const win = window.open(url, '_blank')
  // Fallback if popup was blocked (common in event-delegated handlers)
  if (!win) window.location.href = url
}

const popupActions = [
  {
    id: 'view-target-area',
    label: t('views.training_center_group.target_areas.view_target_area'),
    onClick: (ta) => {
      const tcId = findTrainingCenterId(ta)
      if (tcId) navigateTo(`/training_centers/${tcId}/target_area`)
    },
  },
  {
    id: 'view-training-center',
    label: t('views.training_center_group.target_areas.view_training_center'),
    onClick: (ta) => {
      const tcId = findTrainingCenterId(ta)
      if (tcId) navigateTo(`/training_centers/${tcId}`)
    },
  },
]

// ─── Mount ────────────────────────────────────────────────────────────────────

onMounted(async () => {
  if (!props.targetAreasData?.length) return

  try {
    await fetchToken()
  } catch (err) {
    console.error('[TargetAreasV2] Failed to fetch token:', err)
    FlashMessageService.error('Failed to load map. Please try again.')
  }
})
</script>
